<script lang="ts">
  import { ChevronDown } from "../icons/index.js";
  import { Menu } from "./menu/index.js";
  import type { HTMLSelectAttributes } from "svelte/elements";
  import type { Except } from "type-fest";

  export interface SelectOption {
    value: string;
    label: string;
    description?: string;
  }

  interface Props extends Except<HTMLSelectAttributes, "size" | "disabled"> {
    options?: SelectOption[];
    value?: string;
    size?: "sm" | "md";
    variant?: "default" | "ghost";
    disabled?: boolean;
    class?: string;
    placeholder?: string;
    searchable?: boolean;
    sortFunc?: (a: SelectOption, b: SelectOption) => number;
    onChange?: (option: SelectOption) => void;
  }

  let {
    options = [],
    value = $bindable(""),
    size = "md",
    disabled = false,
    variant = "default",
    class: className,
    placeholder,
    searchable = false,
    sortFunc = (a, b) => a.label.localeCompare(b.label),
    onChange,
    ...rest
  }: Props = $props();

  const selectedLabel = $derived(options.find((opt) => opt.value === value)?.label);

  const sortedOptions = $derived([...options].sort(sortFunc));

  function handleSelect(option: SelectOption) {
    if (onChange) {
      onChange(option);
    } else {
      value = option.value;
    }
  }

  const uid = $props.id();
</script>

<div
  class={[
    variant,
    "wrapper",
    size,
    "relative flex min-w-40 gap-2 rounded-lg text-sm outline-transparent transition-colors duration-200",
    className,
  ]}
  class:disabled
>
  <Menu
    direction="bottom-start"
    portal="body"
    sameWidth={true}
    typeahead={!searchable}
    disableFocusFirstItem={searchable}
  >
    <Menu.Trigger
      aria-labelledby={uid}
      class="trigger absolute inset-0 h-full w-full cursor-pointer opacity-0"
      {disabled}
      variant="unstyled"
    ></Menu.Trigger>

    <select
      id="select"
      aria-labelledby={uid}
      bind:value
      {disabled}
      class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      style="pointer-events: none;"
      tabindex={-1}
      onchange={(e) => {
        const target = e.target as HTMLSelectElement;
        const option = options.find((opt) => opt.value === target.value);
        if (option) handleSelect(option);
      }}
      {...rest}
    >
      {#each sortedOptions as option}
        <option value={option.value}>{option.label}</option>
      {/each}
    </select>
    <div
      class="pointer-events-none flex h-full w-full items-center pr-10 pl-3 {className}"
      id={uid}
    >
      <span class="truncate">
        {#if selectedLabel}
          {selectedLabel}
        {:else if placeholder}
          {placeholder}
        {:else}
          Select an option...
        {/if}
      </span>
    </div>
    <div
      aria-hidden="true"
      class="pointer-events-none absolute top-1/2 right-2 z-10 -translate-y-1/2 text-subtle"
    >
      <ChevronDown />
    </div>

    <Menu.Content class="outline-none">
      <Menu.SearchableList
        autoFocus={searchable}
        emptyText={searchable ? "No options found" : undefined}
        getLabel={(opt) => opt.label}
        items={sortedOptions}
        maxHeight="240px"
        {searchable}
      >
        {#snippet item(option)}
          <Menu.Item onSelect={() => handleSelect(option)}>
            <div class="flex flex-col">
              <span>{option.label}</span>
              {#if option.description}
                <span class="text-xs text-mono-000/60 dark:text-mono-900/60">
                  {option.description}
                </span>
              {/if}
            </div>
          </Menu.Item>
        {/snippet}
      </Menu.SearchableList>
    </Menu.Content>
  </Menu>
</div>

<style>
  @reference "#tailwind.css";
  .wrapper {
    &.default {
      @apply bg-mono-000 shadow-border-inset focus-within:outline focus-within:outline-focus-subtle;
    }

    &.ghost {
      @apply hover:bg-mono-300;

      &:has(:global(:focus-visible)) {
        @apply bg-mono-300;
      }
    }

    &.sm {
      height: var(--spacing-input-height-sm);
    }

    &.md {
      height: var(--spacing-input-height-md);
    }

    &.disabled {
      background: var(--color-mono-300);
      cursor: not-allowed;
      select {
        cursor: not-allowed;
      }
    }

    &:not(.disabled) {
      &:has(:global(.trigger):focus-visible) {
        outline: 2px solid var(--color-focus);
        outline-offset: 2px;
      }
    }
  }

  .wrapper :global(.menu) {
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    left: 0 !important;
    transform: translateX(0) !important;
  }
</style>
